import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { open as openDialog } from '@tauri-apps/plugin-dialog';
import { Trash2, Plus, FolderOpen } from 'lucide-react';
import { Section } from '../settingsPrimitives';
import { useCertificateStore } from '../../store/certificateStore';
import { isTauri } from '../../utils/tauri';
import SslVerificationSetting from './SslVerificationSetting';

/**
 * Settings → Certificates. Moved out of SettingsModal.js and localized
 * (en + de) on 24.09.2026; the SSL verification section is the shared
 * SslVerificationSetting, the same switch Settings → Requests shows.
 */

// In-memory passphrase store (session only — never persisted).
const sessionPassphrases = {};

const CertificatesSettings = () => {
  const { t } = useTranslation();
  const { clientCerts, caCerts, addClientCert, removeClientCert,
          addCACert, removeCACert } = useCertificateStore();

  // ── Add client cert form state ───────────────────────────────────────────────
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientForm, setClientForm] = useState({
    domain: '', type: 'pfx', certPath: '', certFileName: '',
    keyPath: '', keyFileName: '', passphrase: '', label: '',
  });
  const [clientFormError, setClientFormError] = useState(null);
  const [clientFormSaving, setClientFormSaving] = useState(false);

  // ── Add CA cert form state ───────────────────────────────────────────────────
  const [showAddCA, setShowAddCA] = useState(false);
  const [caForm, setCAForm] = useState({ path: '', fileName: '', label: '' });
  const [caFormError, setCAFormError] = useState(null);
  const [caFormSaving, setCAFormSaving] = useState(false);

  const desktopApp = isTauri();

  // ── File picker helpers ──────────────────────────────────────────────────────
  const pickCertFile = async (field) => {
    if (!desktopApp) return;
    try {
      const selected = await openDialog({
        multiple: false,
        filters: [
          { name: t('certificates.filterCertificates'), extensions: ['pfx', 'p12', 'pem', 'crt', 'cer', 'key'] },
          { name: t('certificates.filterAll'), extensions: ['*'] },
        ],
      });
      if (!selected) return;
      const filePath = typeof selected === 'string' ? selected : selected.path || selected;
      const fileName = filePath.split(/[\\/]/).pop();
      if (field === 'cert') {
        setClientForm((f) => ({ ...f, certPath: filePath, certFileName: fileName }));
      } else if (field === 'key') {
        setClientForm((f) => ({ ...f, keyPath: filePath, keyFileName: fileName }));
      } else if (field === 'ca') {
        setCAForm((f) => ({ ...f, path: filePath, fileName }));
      }
    } catch (err) {
      // User cancelled or dialog failed — ignore silently
    }
  };

  // ── Save client cert ─────────────────────────────────────────────────────────
  const handleSaveClientCert = async () => {
    setClientFormError(null);
    if (!clientForm.domain.trim()) {
      setClientFormError(t('certificates.errDomainRequired'));
      return;
    }
    if (!clientForm.certPath) {
      setClientFormError(t('certificates.errCertRequired'));
      return;
    }
    if (clientForm.type === 'pem' && !clientForm.keyPath) {
      setClientFormError(t('certificates.errKeyRequired'));
      return;
    }
    setClientFormSaving(true);
    try {
      const id = `cert_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      let storedCertPath = clientForm.certPath;
      let storedKeyPath = clientForm.keyPath || null;

      if (desktopApp) {
        storedCertPath = await invoke('save_certificate_file', {
          sourcePath: clientForm.certPath,
          certId: id,
          fileType: 'cert',
        });
        if (clientForm.type === 'pem' && clientForm.keyPath) {
          storedKeyPath = await invoke('save_certificate_file', {
            sourcePath: clientForm.keyPath,
            certId: id,
            fileType: 'key',
          });
        }
      }

      // Store passphrase in session memory (not in the Zustand persist store)
      if (clientForm.passphrase) {
        sessionPassphrases[id] = clientForm.passphrase;
      }

      addClientCert({
        id,
        domain: clientForm.domain.trim(),
        type: clientForm.type,
        certPath: storedCertPath,
        keyPath: storedKeyPath,
        label: clientForm.label.trim() || clientForm.certFileName,
      });

      setClientForm({ domain: '', type: 'pfx', certPath: '', certFileName: '',
                      keyPath: '', keyFileName: '', passphrase: '', label: '' });
      setShowAddClient(false);
    } catch (err) {
      setClientFormError(typeof err === 'string' ? err : (err?.message || t('certificates.errSaveFailed')));
    } finally {
      setClientFormSaving(false);
    }
  };

  const handleRemoveClientCert = async (cert) => {
    if (desktopApp) {
      try { await invoke('delete_certificate_file', { certId: cert.id }); } catch { /* ignore */ }
    }
    delete sessionPassphrases[cert.id];
    removeClientCert(cert.id);
  };

  // ── Save CA cert ─────────────────────────────────────────────────────────────
  const handleSaveCACert = async () => {
    setCAFormError(null);
    if (!caForm.path) {
      setCAFormError(t('certificates.errCaRequired'));
      return;
    }
    setCAFormSaving(true);
    try {
      const id = `ca_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      let storedPath = caForm.path;

      if (desktopApp) {
        storedPath = await invoke('save_certificate_file', {
          sourcePath: caForm.path,
          certId: id,
          fileType: 'ca',
        });
      }

      addCACert({
        id,
        path: storedPath,
        label: caForm.label.trim() || caForm.fileName,
      });

      setCAForm({ path: '', fileName: '', label: '' });
      setShowAddCA(false);
    } catch (err) {
      setCAFormError(typeof err === 'string' ? err : (err?.message || t('certificates.errCaSaveFailed')));
    } finally {
      setCAFormSaving(false);
    }
  };

  const handleRemoveCACert = async (ca) => {
    if (desktopApp) {
      try { await invoke('delete_certificate_file', { certId: ca.id }); } catch { /* ignore */ }
    }
    removeCACert(ca.id);
  };

  return (
    <>
      {/* ── Client Certificates ─────────────────────────────────────────────── */}
      <Section title={t('certificates.clientTitle')}>
        <div className="settings-row-desc" style={{ marginBottom: 12 }}>
          {t('certificates.clientDesc')}
        </div>

        {clientCerts.length > 0 && (
          <div className="cert-table">
            <div className="cert-table-header">
              <span>{t('certificates.domain')}</span>
              <span>{t('certificates.type')}</span>
              <span>{t('certificates.label')}</span>
              <span />
            </div>
            {clientCerts.map((cert) => (
              <div key={cert.id} className="cert-table-row">
                <span className="cert-table-domain">{cert.domain}</span>
                <span className="cert-table-type">{cert.type.toUpperCase()}</span>
                <span className="cert-table-label">{cert.label || '—'}</span>
                <button
                  type="button"
                  className="cert-delete-btn"
                  onClick={() => handleRemoveClientCert(cert)}
                  aria-label={t('certificates.deleteCert')}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!showAddClient ? (
          <button
            type="button"
            className="settings-action-btn"
            style={{ marginTop: clientCerts.length > 0 ? 10 : 0 }}
            onClick={() => { setShowAddClient(true); setClientFormError(null); }}
          >
            <Plus size={13} />
            {t('certificates.addCert')}
          </button>
        ) : (
          <div className="cert-add-form">
            <div className="cert-form-row">
              <label className="cert-form-label">{t('certificates.domain')}</label>
              <input
                type="text"
                className="settings-text-input"
                style={{ width: 200 }}
                placeholder={t('certificates.domainPlaceholder')}
                value={clientForm.domain}
                onChange={(e) => setClientForm((f) => ({ ...f, domain: e.target.value }))}
              />
            </div>
            <div className="cert-form-row">
              <label className="cert-form-label">{t('certificates.type')}</label>
              <div className="settings-segmented">
                {['pfx', 'pem'].map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setClientForm((f) => ({ ...f, type: kind }))}
                    className={`settings-segmented-btn${clientForm.type === kind ? ' settings-segmented-btn--active' : ''}`}
                  >
                    {kind === 'pfx' ? 'PFX / PKCS12' : 'PEM'}
                  </button>
                ))}
              </div>
            </div>

            {clientForm.type === 'pfx' ? (
              <>
                <div className="cert-form-row">
                  <label className="cert-form-label">{t('certificates.pfxFile')}</label>
                  <div className="cert-file-picker">
                    <button type="button" className="settings-action-btn" onClick={() => pickCertFile('cert')}>
                      <FolderOpen size={13} />
                      {t('certificates.browse')}
                    </button>
                    {clientForm.certFileName && <span className="cert-file-name">{clientForm.certFileName}</span>}
                  </div>
                </div>
                <div className="cert-form-row">
                  <label className="cert-form-label">{t('certificates.passphrase')}</label>
                  <input
                    type="password"
                    className="settings-text-input"
                    style={{ width: 200 }}
                    placeholder={t('certificates.blankIfNone')}
                    value={clientForm.passphrase}
                    onChange={(e) => setClientForm((f) => ({ ...f, passphrase: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="cert-form-row">
                  <label className="cert-form-label">{t('certificates.pemCert')}</label>
                  <div className="cert-file-picker">
                    <button type="button" className="settings-action-btn" onClick={() => pickCertFile('cert')}>
                      <FolderOpen size={13} />
                      {t('certificates.browse')}
                    </button>
                    {clientForm.certFileName && <span className="cert-file-name">{clientForm.certFileName}</span>}
                  </div>
                </div>
                <div className="cert-form-row">
                  <label className="cert-form-label">{t('certificates.pemKey')}</label>
                  <div className="cert-file-picker">
                    <button type="button" className="settings-action-btn" onClick={() => pickCertFile('key')}>
                      <FolderOpen size={13} />
                      {t('certificates.browse')}
                    </button>
                    {clientForm.keyFileName && <span className="cert-file-name">{clientForm.keyFileName}</span>}
                  </div>
                </div>
                <div className="cert-form-row">
                  <label className="cert-form-label">{t('certificates.keyPassphrase')}</label>
                  <input
                    type="password"
                    className="settings-text-input"
                    style={{ width: 200 }}
                    placeholder={t('certificates.blankIfNone')}
                    value={clientForm.passphrase}
                    onChange={(e) => setClientForm((f) => ({ ...f, passphrase: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
              </>
            )}

            <div className="cert-form-row">
              <label className="cert-form-label">{t('certificates.label')}</label>
              <input
                type="text"
                className="settings-text-input"
                style={{ width: 200 }}
                placeholder={t('certificates.labelPlaceholder')}
                value={clientForm.label}
                onChange={(e) => setClientForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>

            {clientFormError && (
              <div className="privacy-feedback privacy-feedback--error" style={{ paddingTop: 4 }}>{clientFormError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, paddingTop: 12 }}>
              <button type="button" className="settings-action-btn" onClick={handleSaveClientCert} disabled={clientFormSaving}>
                {clientFormSaving ? t('certificates.saving') : t('common.save')}
              </button>
              <button type="button" className="settings-action-btn" onClick={() => { setShowAddClient(false); setClientFormError(null); }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* ── CA Certificates ──────────────────────────────────────────────────── */}
      <Section title={t('certificates.caTitle')}>
        <div className="settings-row-desc" style={{ marginBottom: 12 }}>
          {t('certificates.caDesc')}
        </div>

        {caCerts.length > 0 && (
          <div className="cert-table cert-table--ca">
            <div className="cert-table-header">
              <span>{t('certificates.label')}</span>
              <span />
            </div>
            {caCerts.map((ca) => (
              <div key={ca.id} className="cert-table-row">
                <span className="cert-table-label">{ca.label || ca.path.split(/[\\/]/).pop()}</span>
                <button
                  type="button"
                  className="cert-delete-btn"
                  onClick={() => handleRemoveCACert(ca)}
                  aria-label={t('certificates.deleteCa')}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!showAddCA ? (
          <button
            type="button"
            className="settings-action-btn"
            style={{ marginTop: caCerts.length > 0 ? 10 : 0 }}
            onClick={() => { setShowAddCA(true); setCAFormError(null); }}
          >
            <Plus size={13} />
            {t('certificates.addCa')}
          </button>
        ) : (
          <div className="cert-add-form">
            <div className="cert-form-row">
              <label className="cert-form-label">{t('certificates.caFile')}</label>
              <div className="cert-file-picker">
                <button type="button" className="settings-action-btn" onClick={() => pickCertFile('ca')}>
                  <FolderOpen size={13} />
                  {t('certificates.browse')}
                </button>
                {caForm.fileName && <span className="cert-file-name">{caForm.fileName}</span>}
              </div>
            </div>
            <div className="cert-form-row">
              <label className="cert-form-label">{t('certificates.label')}</label>
              <input
                type="text"
                className="settings-text-input"
                style={{ width: 200 }}
                placeholder={t('certificates.caLabelPlaceholder')}
                value={caForm.label}
                onChange={(e) => setCAForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>

            {caFormError && (
              <div className="privacy-feedback privacy-feedback--error" style={{ paddingTop: 4 }}>{caFormError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, paddingTop: 12 }}>
              <button type="button" className="settings-action-btn" onClick={handleSaveCACert} disabled={caFormSaving}>
                {caFormSaving ? t('certificates.saving') : t('common.save')}
              </button>
              <button type="button" className="settings-action-btn" onClick={() => { setShowAddCA(false); setCAFormError(null); }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* ── SSL verification — the SAME switch as Settings → Requests ─────────── */}
      <Section title={t('certificates.sslTitle')}>
        <SslVerificationSetting t={t} />
      </Section>
    </>
  );
};

export default CertificatesSettings;
